import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { useAuth } from "@/contexts/AuthContext";
import AppLayout from "@/components/AppLayout";
import { Users, CheckCircle2, AlertTriangle, LogIn } from "lucide-react";
import { toast } from "sonner";

// Landing for the email-invite link `/team-invite?code=XYZ`.
// Handles four states:
//   1) code missing → error
//   2) not logged in → show peek data + link to /login
//   3) logged in with wrong email → show peek + hint
//   4) logged in with correct email → auto-accept, redirect to club
export default function TeamInvitePage() {
  const t = useT();
  const nav = useNavigate();
  const [search] = useSearchParams();
  const code = search.get("code") || "";
  const { user, loading: authLoading } = useAuth();
  const [peek, setPeek] = useState(null);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    if (!code) { setError(t("Uitnodigingscode ontbreekt in de link.")); return; }
    api.get(`/team-invites/${code}`)
      .then(({ data }) => setPeek(data))
      .catch((e) => setError(formatApiError(e.response?.data?.detail)));
  }, [code, t]);

  // Auto-accept when the current user matches the invited email.
  useEffect(() => {
    if (authLoading || !peek || !user) return;
    if (peek.already_accepted) {
      // Already active → just go to the club.
      nav(`/clubs/${peek.club_id}`);
      return;
    }
    if (peek.expired) return;
    if ((user.email || "").toLowerCase() !== (peek.invited_email || "").toLowerCase()) return;
    setAccepting(true);
    api.post(`/team-invites/${code}/accept`)
      .then(({ data }) => {
        toast.success(t("Uitnodiging geaccepteerd — welkom bij het team."));
        nav(`/clubs/${data.club_id}`);
      })
      .catch((e) => {
        setError(formatApiError(e.response?.data?.detail));
        setAccepting(false);
      });
  }, [authLoading, peek, user, code, nav, t]);

  return (
    <AppLayout>
      <div className="max-w-xl mx-auto mt-8 border-2 border-foreground bg-card heavy-shadow p-8" data-testid="team-invite-page">
        <div className="stat-label flex items-center gap-2">
          <Users className="w-4 h-4" /> {t("Team-uitnodiging")}
        </div>
        <h1 className="font-display font-black text-3xl uppercase tracking-tight mt-1 mb-3">
          {t("Meewerken aan MeerjarenplanOnline.nl")}
        </h1>

        {error && (
          <div className="p-4 border-2 border-destructive bg-destructive/10 flex items-start gap-2 text-sm" data-testid="team-invite-error">
            <AlertTriangle className="w-5 h-5 shrink-0" /> <span>{error}</span>
          </div>
        )}

        {peek && !error && (
          <>
            <div className="grid gap-3 text-sm mb-4">
              <div>
                <div className="stat-label">{t("Club")}</div>
                <div className="font-display font-black text-xl" data-testid="team-invite-club">{peek.club_name}</div>
              </div>
              <div>
                <div className="stat-label">{t("Uitnodiging voor")}</div>
                <div className="font-mono-num" data-testid="team-invite-email">{peek.invited_email}</div>
              </div>
              <div>
                <div className="stat-label">{t("Rol")}</div>
                <div className="font-display font-bold uppercase text-primary" data-testid="team-invite-role">
                  {peek.role === "editor" ? t("Editor — bekijken en bewerken") : t("Viewer — alleen bekijken")}
                </div>
              </div>
            </div>

            {peek.expired && (
              <div className="p-4 border-2 border-destructive bg-destructive/10 text-sm">
                {t("Deze uitnodiging is verlopen. Vraag de eigenaar om een nieuwe.")}
              </div>
            )}

            {!peek.expired && peek.already_accepted && (
              <div className="p-4 border-2 border-positive bg-positive/10 flex items-center gap-2 text-sm">
                <CheckCircle2 className="w-5 h-5" /> {t("Uitnodiging al geaccepteerd — je wordt doorgestuurd naar de club.")}
              </div>
            )}

            {!peek.expired && !peek.already_accepted && !user && (
              <div className="p-4 border-2 border-primary bg-primary/5 text-sm">
                <p className="mb-3">{t("Log in of registreer met ")} <b className="font-mono-num">{peek.invited_email}</b> {t("om de uitnodiging te accepteren.")}</p>
                <div className="flex gap-2 flex-wrap">
                  <Link
                    to={`/login?next=${encodeURIComponent(`/team-invite?code=${code}`)}`}
                    data-testid="team-invite-login"
                    className="btn-brutal bg-primary text-primary-foreground px-4 py-2 font-display uppercase font-bold text-sm inline-flex items-center gap-2"
                  >
                    <LogIn className="w-4 h-4" /> {t("Inloggen")}
                  </Link>
                  <Link
                    to="/join-the-team"
                    className="btn-brutal bg-background text-foreground border-2 border-foreground px-4 py-2 font-display uppercase font-bold text-sm"
                  >
                    {t("Registreren")}
                  </Link>
                </div>
              </div>
            )}

            {!peek.expired && !peek.already_accepted && user &&
              (user.email || "").toLowerCase() !== (peek.invited_email || "").toLowerCase() && (
              <div className="p-4 border-2 border-secondary bg-secondary/10 text-sm">
                <b className="block mb-1">{t("Ander e-mailadres gedetecteerd")}</b>
                {t("Je bent momenteel ingelogd als ")}<b className="font-mono-num">{user.email}</b>{t(", maar deze uitnodiging is voor ")}<b className="font-mono-num">{peek.invited_email}</b>.
                {t(" Log uit en log opnieuw in met het juiste e-mailadres om de uitnodiging te accepteren.")}
              </div>
            )}

            {!peek.expired && !peek.already_accepted && user &&
              (user.email || "").toLowerCase() === (peek.invited_email || "").toLowerCase() && accepting && (
              <div className="p-4 border-2 border-positive bg-positive/10 text-sm">
                {t("Uitnodiging accepteren…")}
              </div>
            )}
          </>
        )}
      </div>
    </AppLayout>
  );
}
